import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon.jsx';
import { connectRealtime } from './realtime.js';
import { muted } from './alertPrefs.js';

/**
 * Servis uyarıları: "yeni sipariş", "misafir hesabı istedi", "garson çağrıldı"
 * ve "misafir iptal istiyor".
 *
 * NEDEN BÖYLE:
 *  · Uyarı SES + YAZI birlikte: mutfakta ekrana bakılmıyor, salonda ses
 *    duyulmuyor. İkisi birden olunca ikisi de yakalar.
 *  · Ses dosyası YOK: iki notalık bip WebAudio ile üretiliyor — paket
 *    şişmiyor, çevrimdışı da çalıyor. Tarayıcı ilk kullanıcı dokunuşundan
 *    önce ses çalmaya izin vermez; panel zaten dokunarak kullanılıyor.
 *  · Kart TIKLANIR: sipariş → mutfak, hesap → salon. Uyarı "haber" değil, işe
 *    giden kapı.
 *  · En fazla üç kart durur ve kendi kendine kapanır: akşam servisinde
 *    uyarılar ekranı kaplamasın.
 */
const ALERT_ICON = { order: 'bell', bill: 'receipt', waiter: 'users', cancel: 'ban' };
const LIFE_MS = 20_000;
const MAX = 3;

function beep() {
  if (muted()) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [880, 1175].forEach((hz, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = hz;
      g.gain.setValueAtTime(0.0001, now + i * 0.18);
      g.gain.exponentialRampToValueAtTime(0.22, now + i * 0.18 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.18 + 0.16);
      o.connect(g).connect(ctx.destination);
      o.start(now + i * 0.18);
      o.stop(now + i * 0.18 + 0.2);
    });
    setTimeout(() => ctx.close(), 900);
  } catch { /* ses çalınamadıysa yazı yine duruyor */ }
}

export default function Alerts({ t }) {
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const seq = useRef(0);

  useEffect(() => {
    const s = connectRealtime();
    if (!s) return undefined;

    const push = (a) => {
      const id = ++seq.current;
      setItems((prev) => [...prev.slice(-(MAX - 1)), { ...a, id }]);
      beep();
      setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== id)), LIFE_MS);
    };
    const onOrder = (p) => {
      // Personelin kendi girdiği sipariş duyurulmaz: girenin zaten haberi var,
      // akşam servisinde her kalem için kart çıkması gürültü olur. Mutfak
      // ekranı bu olayla yine tazelenir (pages/Kitchen.jsx).
      if (p.channel === 'STAFF') return;
      push({
        kind: 'order',
        title: t('alert.newOrder'),
        body: [p.tableName, p.code && `#${p.code}`].filter(Boolean).join(' · '),
        to: '/kitchen',
      });
    };
    const onBill = (p) => push({
      kind: 'bill',
      title: t('alert.billRequested'),
      body: [p.tableName, p.code && `#${p.code}`].filter(Boolean).join(' · '),
      to: p.sessionId ? `/sessions/${p.sessionId}` : '/tables',
    });
    // Garson çağrısı salona gider: çağrı masanın üstünde de duruyor (kart
    // kaybolsa bile salon ekranında rozet kalır).
    const onWaiter = (p) => push({
      kind: 'waiter',
      title: t('alert.waiterCalled'),
      body: p.tableName || '',
      to: '/tables',
    });
    // İptal İSTEĞİ mutfağa gider: kararı orada veren kişi görüyor.
    const onCancelReq = (p) => push({
      kind: 'cancel',
      title: t('alert.cancelRequested'),
      body: [p.tableName, p.code && `#${p.code}`].filter(Boolean).join(' · '),
      to: '/kitchen',
    });

    s.on('orders:new', onOrder);
    s.on('staff:bill-requested', onBill);
    s.on('staff:waiter-called', onWaiter);
    s.on('staff:cancel-requested', onCancelReq);
    return () => {
      s.off('orders:new', onOrder);
      s.off('staff:bill-requested', onBill);
      s.off('staff:waiter-called', onWaiter);
      s.off('staff:cancel-requested', onCancelReq);
    };
    // eslint-disable-next-line
  }, []);

  if (!items.length) return null;

  return (
    <div className="alerts" role="status" aria-live="polite">
      {items.map((a) => (
        <div className="alert" key={a.id}>
          <button className="alert-go" onClick={() => { nav(a.to); setItems((p) => p.filter((x) => x.id !== a.id)); }}>
            <Icon n={ALERT_ICON[a.kind] || 'bell'} />
            <span>
              <b>{a.title}</b>
              {a.body && <em>{a.body}</em>}
            </span>
          </button>
          <button className="alert-x" aria-label={t('close')} title={t('close')}
            onClick={() => setItems((p) => p.filter((x) => x.id !== a.id))}>
            <Icon n="x" />
          </button>
        </div>
      ))}
    </div>
  );
}
